import React from 'react';
import {
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
} from '@mui/icons-material';

export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  if (imageUrl.startsWith('file://')) return '';
  const apiUrl = process.env.REACT_APP_API_URL || 'https://congrow-beta-production.up.railway.app';
  const normalizedPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  return `${apiUrl}${normalizedPath}`;
};

export const getApprovalColor = (status) => {
  switch (status) {
    case 'approved': return 'success';
    case 'rejected': return 'error';
    default: return 'warning';
  }
};

export const formatFieldName = (fieldName) => {
  const fieldMap = {
    title: 'Title',
    type: 'Type',
    cost: 'Cost',
    image_url: 'Image',
    status: 'Status',
    contractor_approval: 'Contractor Approval',
    homeowner_approval: 'Homeowner Approval',
    admin_approval: 'Admin Approval',
    approval_deadline: 'Approval Deadline',
    change_order: 'Change Order',
  };
  return fieldMap[fieldName] || fieldName;
};

export const formatFieldValue = (fieldName, value) => {
  if (fieldName === 'cost') return `$${parseFloat(value).toLocaleString()}`;
  if (fieldName === 'type') {
    const typeMap = { labor: 'Labor', materials: 'Materials', both: 'Materials and Labor' };
    return typeMap[value] || value;
  }
  if (fieldName === 'approval_deadline') {
    if (value === 'none' || !value) return 'None';
    return new Date(value).toLocaleString();
  }
  return value;
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const getFileIcon = (fileType) => {
  if (!fileType) return <FileIcon />;
  if (fileType.startsWith('image/')) return <ImageIcon color="primary" />;
  if (fileType === 'application/pdf') return <PdfIcon color="error" />;
  if (fileType.includes('document') || fileType.includes('word')) return <DocIcon color="info" />;
  if (fileType.includes('spreadsheet') || fileType.includes('excel')) return <DocIcon color="success" />;
  return <FileIcon />;
};

export const getDeadlineStatus = (item) => {
  if (!item.approval_deadline) return null;
  const deadline = new Date(item.approval_deadline);
  const now = new Date();
  const hoursUntilDeadline = (deadline - now) / (1000 * 60 * 60);
  const allApproved =
    item.contractor_approval === 'approved' &&
    item.homeowner_approval === 'approved' &&
    item.admin_approval === 'approved';
  if (allApproved) return null;
  if (hoursUntilDeadline < 0) return { status: 'overdue', color: 'error', label: 'OVERDUE' };
  if (hoursUntilDeadline < 24) return { status: 'urgent', color: 'warning', label: 'DUE SOON' };
  if (hoursUntilDeadline < 72) return { status: 'upcoming', color: 'info', label: `Due ${deadline.toLocaleDateString()}` };
  return { status: 'normal', color: 'default', label: `Due ${deadline.toLocaleDateString()}` };
};

export const organizeItemsWithChangeOrders = (items, filter, userRole) => {
  const parentItems = items.filter(item => !item.is_change_order);
  const changeOrders = items.filter(item => item.is_change_order);

  const matchesFilter = (item) => {
    if (filter === 'all') return true;
    let approvalStatus;
    if (userRole === 'contractor') approvalStatus = item.contractor_approval;
    else if (userRole === 'homeowner') approvalStatus = item.homeowner_approval;
    else if (userRole === 'admin') approvalStatus = item.admin_approval;
    if (filter === 'approved') return approvalStatus === 'approved';
    if (filter === 'rejected') return approvalStatus === 'rejected';
    if (filter === 'outstanding') return approvalStatus === 'outstanding';
    return true;
  };

  const organized = parentItems.map(item => ({
    ...item,
    changeOrders: changeOrders.filter(co => co.parent_item_id === item.id),
  }));

  if (filter === 'all') return organized;

  return organized
    .filter(item => matchesFilter(item) || item.changeOrders.some(co => matchesFilter(co)))
    .map(item => ({ ...item, changeOrders: item.changeOrders.filter(co => matchesFilter(co)) }));
};
